'use client';

import Link from 'next/link';
import { Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';
import { Separator } from '@kit/ui/separator';

import { AskFlowLogo } from '~/components/askflow-logo';
import {
  CONTACT_EMAIL,
  INSTAGRAM_URL,
  MARKETING_ORG_NAME,
} from '~/lib/marketing/site';
import { localizedHref } from '~/lib/marketing/routes';

/*
 * Kept short on purpose: every vertical, then the hubs (guides, customers,
 * FAQ). Comparisons, buying guides, region pages and single guides are linked
 * from page content and the guides hub, and all of them are in the sitemap.
 * Labels are bilingual inline (content-file pattern); the shared `landing`
 * namespace only carries the legacy keys.
 */
const FOOTER_COLUMNS = [
  {
    heading: { en: 'Solutions', ar: 'الحلول' },
    links: [
      {
        label: { en: 'Padel & tennis clubs', ar: 'أندية البادل والتنس' },
        href: '/padel-software',
      },
      {
        label: { en: 'Pilates & yoga studios', ar: 'استوديوهات البيلاتس واليوغا' },
        href: '/pilates-studio-software',
      },
      {
        label: { en: 'Clinics & wellness', ar: 'العيادات ومراكز العافية' },
        href: '/clinic-software',
      },
      { label: { en: 'Gyms', ar: 'الصالات الرياضية' }, href: '/gym-software' },
      {
        label: { en: 'Membership clubs', ar: 'أندية العضويات' },
        href: '/membership-club-software',
      },
      { label: { en: 'Sports academies', ar: 'الأكاديميات الرياضية' }, href: '/academy-software' },
      {
        label: { en: 'Enterprise & schools', ar: 'المؤسسات والمدارس' },
        href: '/enterprise',
      },
    ],
  },
  {
    heading: { en: 'Resources', ar: 'المصادر' },
    links: [
      { label: { en: 'Platform tour', ar: 'جولة في المنصة' }, href: '/#platform' },
      { label: { en: 'Guides', ar: 'الأدلة' }, href: '/guides' },
      { label: { en: 'Our Customers', ar: 'عملاؤنا' }, href: '/customers' },
      { label: { en: 'FAQ', ar: 'الأسئلة الشائعة' }, href: '/faq' },
    ],
  },
] as const;

export function MarketingFooter() {
  const { t, i18n } = useTranslation('landing');
  const isRTL = i18n.language === 'ar';
  const locale = isRTL ? 'ar' : 'en';

  return (
    <footer
      dir={isRTL ? 'rtl' : 'ltr'}
      className="relative mt-auto w-full py-6 bg-[#ede8e2] text-[#202020]"
    >
      <div className="container mx-auto px-4">
        <div className="mt-2 md:mt-4 lg:mt-6 flex flex-col lg:space-y-6">
          {/* Logo */}
          <div className="flex w-full flex-col sm:mb-0 md:space-y-4 lg:w-4/12">
            <Link href={localizedHref('/', locale)} className="block" aria-label="AskFlow by ASK AI">
              <AskFlowLogo className="h-8 md:h-10 text-[#202020]" />
            </Link>
          </div>

          <Separator className="my-4 lg:my-6 h-[3px] bg-[#dcd7cf]" />

          {/* Main Content */}
          <div className="flex w-full flex-col md:flex-row justify-between items-start gap-y-8 md:gap-8">
            {/* Contact Info */}
            <div className="flex flex-col space-y-2">
              <span className="font-heading text-md font-semibold tracking-wider">
                Flow
              </span>
              <div className="flex flex-col text-md text-[#202020]/80">
                <span>{t('footer.sportsManagementPlatform')}</span>
              </div>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-semibold flex items-center gap-2 hover:opacity-70 transition-opacity"
              >
                <span>{t('footer.contactUs')}</span>
                <svg
                  width="15"
                  height="14"
                  viewBox="0 0 15 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={isRTL ? 'rotate-180' : ''}
                >
                  <path
                    d="M14.6055 7.58887L9.29297 12.9014C9.09375 13.1006 8.82812 13.2002 8.5625 13.2002C8.26367 13.2002 7.99805 13.1006 7.79883 12.9014C7.36719 12.5029 7.36719 11.8057 7.79883 11.4072L11.2852 7.8877H1.125C0.527344 7.8877 0.0625 7.42286 0.0625 6.8252C0.0625 6.26075 0.527344 5.7627 1.125 5.7627H11.2852L7.79883 2.27637C7.36719 1.87793 7.36719 1.18066 7.79883 0.782226C8.19727 0.350586 8.89453 0.350586 9.29297 0.782226L14.6055 6.09473C15.0371 6.49317 15.0371 7.19043 14.6055 7.58887Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            </div>

            {/* Link columns */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
              {FOOTER_COLUMNS.map((column) => (
                <div key={column.heading.en} className="flex flex-col space-y-2">
                  <span className="font-heading text-md font-semibold tracking-wider uppercase">
                    {column.heading[locale]}
                  </span>
                  <div className="flex flex-col space-y-3">
                    {column.links.map((link) => (
                      <Link
                        key={link.href}
                        href={localizedHref(link.href, locale)}
                        className="text-md text-[#202020]/80 hover:text-[#202020] transition-colors"
                      >
                        {link.label[locale]}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}

              {/* Legal + Social */}
              <div className="flex flex-col space-y-2">
                <span className="font-heading text-md font-semibold tracking-wider uppercase">
                  {t('footer.legal')}
                </span>
                <div className="flex flex-col space-y-3">
                  <a
                    href="/privacy-policy"
                    className="text-md text-[#202020]/80 hover:text-[#202020] transition-colors"
                  >
                    {t('footer.privacyPolicy')}
                  </a>
                  <a
                    href="/terms-of-service"
                    className="text-md text-[#202020]/80 hover:text-[#202020] transition-colors"
                  >
                    {t('footer.termsOfService')}
                  </a>
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="pt-1"
                  >
                    <Instagram className="w-9 h-9 border-2 border-[#202020]/40 rounded-full p-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-4 lg:mt-0 lg:mb-6 h-[3px] bg-[#dcd7cf]" />

          {/* Copyright */}
          <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="text-sm">
              <Trans
                i18nKey="marketing:copyright"
                values={{
                  product: MARKETING_ORG_NAME,
                  year: new Date().getFullYear(),
                }}
              />
            </div>
            <div className="flex flex-col items-start md:items-end">
              <div className="text-sm opacity-90">
                {t('footer.poweredByPrefix')}{' '}
                <a
                  href="https://askflow.sa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:opacity-70 transition-opacity"
                >
                  askflow.sa
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
